// Notifications API Service - Phase 4 Day 1
import { apiClient } from './apiClient';

export interface Notification {
  id: string;
  type: 'success' | 'info' | 'warning' | 'error';
  title: string;
  message: string;
  read: boolean;
  action_url?: string;
  action_label?: string;
  created_at: string;
  read_at?: string;
}

export interface NotificationPreference {
  id: string;
  category: string;
  event_type: string;
  label: string;
  description: string;
  in_app: boolean;
  email: boolean;
  push: boolean;
  sound_enabled?: boolean;
}

export interface BulkAction {
  id: string;
  action_type: 'validate' | 'reject' | 'assign' | 'archive';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  target_ids: number[];
  total_count: number;
  processed_count: number;
  success_count: number;
  failed_count: number;
  metadata?: Record<string, any>;
  errors?: Array<{ application_id: number; error: string }>;
  created_at: string;
  completed_at?: string;
}

export interface ExportTask {
  id: string;
  format: 'excel' | 'csv' | 'pdf' | 'json';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  config: Record<string, any>;
  total_records: number;
  processed_records: number;
  file_path?: string;
  file_size?: number;
  download_url?: string;
  error_message?: string;
  created_at: string;
  completed_at?: string;
  expires_at?: string;
}

// Notification API
export const notificationsApi = {
  /**
   * Get all notifications for current user
   */
  async getNotifications(): Promise<Notification[]> {
    const response = await apiClient.get<Notification[]>('/notifications/');
    return response.data;
  },

  /**
   * Get unread notifications count
   */
  async getUnreadCount(): Promise<number> {
    const response = await apiClient.get<{ count: number }>('/notifications/unread_count/');
    return response.data.count;
  },

  /**
   * Mark notification as read
   */
  async markAsRead(id: string): Promise<void> {
    await apiClient.post(`/notifications/${id}/mark_read/`);
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    await apiClient.post('/notifications/mark_all_read/');
  },

  /**
   * Clear all notifications
   */
  async clearAll(): Promise<void> {
    await apiClient.delete('/notifications/clear_all/');
  },

  /**
   * Create a new notification (typically for testing)
   */
  async create(data: Partial<Notification>): Promise<Notification> {
    const response = await apiClient.post<Notification>('/notifications/', data);
    return response.data;
  },
};

// Notification Preferences API
export const notificationPreferencesApi = {
  /**
   * Get all notification preferences for current user
   */
  async getPreferences(): Promise<NotificationPreference[]> {
    const response = await apiClient.get<NotificationPreference[]>('/preferences/');
    return response.data;
  },

  /**
   * Update a preference
   */
  async updatePreference(
    id: string,
    data: Partial<NotificationPreference>
  ): Promise<NotificationPreference> {
    const response = await apiClient.patch<NotificationPreference>(`/preferences/${id}/`, data);
    return response.data;
  },

  /**
   * Bulk update preferences
   */
  async bulkUpdate(preferences: Partial<NotificationPreference>[]): Promise<void> {
    await apiClient.post('/preferences/bulk_update/', { preferences });
  },

  /**
   * Initialize default preferences for user
   */
  async initializeDefaults(): Promise<{ created: number; total: number }> {
    const response = await apiClient.post<{ created: number; total: number }>(
      '/preferences/initialize_defaults/'
    );
    return response.data;
  },
};

// Bulk Actions API
export const bulkActionsApi = {
  /**
   * Create a bulk action
   */
  async create(data: {
    action_type: 'validate' | 'reject' | 'assign' | 'archive';
    target_ids: number[];
    metadata?: Record<string, any>;
  }): Promise<BulkAction> {
    const response = await apiClient.post<BulkAction>('/bulk-actions/', data);
    return response.data;
  },

  /**
   * Get all bulk actions for current user
   */
  async getAll(): Promise<BulkAction[]> {
    const response = await apiClient.get<BulkAction[]>('/bulk-actions/');
    return response.data;
  },

  /**
   * Get a specific bulk action
   */
  async get(id: string): Promise<BulkAction> {
    const response = await apiClient.get<BulkAction>(`/bulk-actions/${id}/`);
    return response.data;
  },

  /**
   * Get progress of a bulk action
   */
  async getProgress(id: string): Promise<{
    status: string;
    total: number;
    processed: number;
    success: number;
    failed: number;
    percentage: number;
  }> {
    const response = await apiClient.get(`/bulk-actions/${id}/progress/`);
    return response.data;
  },

  /**
   * Poll for bulk action completion
   */
  async pollProgress(
    id: string,
    onProgress?: (progress: any) => void,
    interval = 1000
  ): Promise<BulkAction> {
    return new Promise((resolve, reject) => {
      const poll = setInterval(async () => {
        try {
          const progress = await this.getProgress(id);

          if (onProgress) {
            onProgress(progress);
          }

          if (progress.status === 'completed' || progress.status === 'failed') {
            clearInterval(poll);
            const bulkAction = await this.get(id);
            resolve(bulkAction);
          }
        } catch (error) {
          clearInterval(poll);
          reject(error);
        }
      }, interval);
    });
  },
};

// Export Tasks API
export const exportTasksApi = {
  /**
   * Create an export task
   */
  async create(data: {
    format: 'excel' | 'csv' | 'pdf' | 'json';
    config: Record<string, any>;
  }): Promise<ExportTask> {
    const response = await apiClient.post<ExportTask>('/exports/', data);
    return response.data;
  },

  /**
   * Get all export tasks for current user
   */
  async getAll(): Promise<ExportTask[]> {
    const response = await apiClient.get<ExportTask[]>('/exports/');
    return response.data;
  },

  /**
   * Get a specific export task
   */
  async get(id: string): Promise<ExportTask> {
    const response = await apiClient.get<ExportTask>(`/exports/${id}/`);
    return response.data;
  },

  /**
   * Get progress of an export task
   */
  async getProgress(id: string): Promise<{
    status: string;
    total: number;
    processed: number;
    percentage: number;
  }> {
    const response = await apiClient.get(`/exports/${id}/progress/`);
    return response.data;
  },

  /**
   * Download exported file
   */
  async download(id: string): Promise<Blob> {
    const response = await apiClient.get(`/exports/${id}/download/`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Poll for export completion and auto-download
   */
  async pollAndDownload(
    id: string,
    onProgress?: (progress: any) => void,
    interval = 1000
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const poll = setInterval(async () => {
        try {
          const progress = await this.getProgress(id);

          if (onProgress) {
            onProgress(progress);
          }

          if (progress.status === 'completed') {
            clearInterval(poll);
            const blob = await this.download(id);
            resolve(blob);
          } else if (progress.status === 'failed') {
            clearInterval(poll);
            const task = await this.get(id);
            reject(new Error(task.error_message || 'Export failed'));
          }
        } catch (error) {
          clearInterval(poll);
          reject(error);
        }
      }, interval);
    });
  },
};
